/**
 * Sidebar Component
 *
 * Dynamic navigation sidebar using menu from permissionStore.
 * Uses Ant Design Menu with hierarchical structure.
 * Menu is loaded during login and stored in permissionStore.
 *
 * Permission Control:
 * - Menu items are filtered based on user's permissions
 * - Only shows menu items user has access to
 * - Admin users see all menu items
 */
import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, Skeleton, type MenuProps } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { useUIStore } from '@/store';
import { usePermissionStore } from '@/store/permissionStore';
import {
  buildMenuTree,
  type MenuTreeNode,
  type MenuFunctionDto,
} from '@/shared/utils/menu-tree-builder';
import { getMenuIcon } from '@/shared/utils/menu-icon-mapper';
import useIsMobile from '@/shared/hooks/useIsMobile';
import SidebarFooter from '@/shared/components/layout/footer/Footer';
import './Sidebar.css';

const { Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

/**
 * Default menu items shown when loading or on error
 */
const defaultMenuItems: MenuItem[] = [
  {
    key: '/',
    icon: <AppstoreOutlined />,
    label: 'Dashboard',
  },
];

/**
 * Filter menu tree based on user permissions
 * - All users (including admin) only see items they have permission to access
 */
const filterMenuTreeByPermissions = (
  nodes: MenuTreeNode[],
  allowedNodeIds: Set<number>,
  isSuperAdmin: boolean,
): MenuTreeNode[] => {
  const result: MenuTreeNode[] = [];

  for (const node of nodes) {
    // Filter children first
    const filteredChildren =
      node.children && node.children.length > 0
        ? filterMenuTreeByPermissions(
            node.children,
            allowedNodeIds,
            isSuperAdmin,
          )
        : [];

    // Include node if:
    // 1. Node itself has permission, OR
    // 2. Node has filtered children (parent of allowed items)
    const hasPermission = allowedNodeIds.has(node.nodeId);
    const hasAllowedChildren = filteredChildren.length > 0;

    if (hasPermission || hasAllowedChildren) {
      result.push({
        ...node,
        children: filteredChildren.length > 0 ? filteredChildren : undefined,
      });
    }
  }

  return result;
};

/**
 * Transform MenuTreeNode[] to Ant Design MenuItem[]
 */
const transformToMenuItems = (nodes: MenuTreeNode[]): MenuItem[] => {
  return nodes.map(node => {
    const menuItem: MenuItem = {
      key: node.path || node.key,
      icon: getMenuIcon(node.icon),
      label: node.label,
      children:
        node.children && node.children.length > 0
          ? transformToMenuItems(node.children)
          : undefined,
    };
    return menuItem;
  });
};

/**
 * Add parent key to open keys array if it exists
 */
const addParentKey = (openKeys: string[], parentKey?: string): void => {
  if (parentKey) {
    openKeys.push(parentKey);
  }
};

/**
 * Check if item has children
 */
const hasChildren = (item: MenuTreeNode): boolean => {
  return Boolean(item.children && item.children.length > 0);
};

/**
 * Get all keys for items that should be opened by default
 * (parent items of the current selected path)
 */
const getDefaultOpenKeys = (
  nodes: MenuTreeNode[],
  currentPath: string,
): string[] => {
  const openKeys: string[] = [];

  const findPath = (items: MenuTreeNode[], parentKey?: string): boolean => {
    for (const item of items) {
      const itemKey = item.path || item.key;

      if (itemKey === currentPath) {
        addParentKey(openKeys, parentKey);
        return true;
      }

      if (hasChildren(item)) {
        const children = item.children || [];
        if (findPath(children, itemKey)) {
          addParentKey(openKeys, parentKey);
          return true;
        }
      }
    }
    return false;
  };

  findPath(nodes);
  return openKeys;
};

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarCollapsed: collapsed, toggleSidebar } = useUIStore();
  const { isMobile } = useIsMobile();
  const currentYear = new Date().getFullYear();
  const currentVersion = '2.5.1';

  // Get user permissions and menu from store (loaded during login)
  const permissions = usePermissionStore(state => state.permissions);
  const isSuperAdmin = usePermissionStore(state => state.isSuperAdmin);
  const isInitialized = usePermissionStore(state => state.isInitialized);

  // Get menu from permissions (already loaded on login)
  const menuFunctions = useMemo<MenuFunctionDto[]>(() => {
    return permissions?.menuList ?? [];
  }, [permissions]);

  // Build allowed node IDs set from permissions
  const allowedNodeIds = useMemo(() => {
    if (!permissions?.allowedNodeIds) {
      return new Set<number>();
    }
    return new Set(permissions.allowedNodeIds);
  }, [permissions]);

  // Build tree structure from flat array
  const menuTree = useMemo(() => {
    try {
      if (menuFunctions.length === 0) {
        return [];
      }
      return buildMenuTree(menuFunctions);
    } catch {
      return [];
    }
  }, [menuFunctions]);

  // Filter menu tree based on user permissions
  const filteredMenuTree = useMemo(() => {
    if (menuTree.length === 0) {
      return [];
    }
    return filterMenuTreeByPermissions(menuTree, allowedNodeIds, isSuperAdmin);
  }, [menuTree, allowedNodeIds, isSuperAdmin]);

  // Transform filtered menu tree to Ant Design format
  const menuItems = useMemo<MenuItem[]>(() => {
    try {
      if (filteredMenuTree.length === 0) {
        return defaultMenuItems;
      }
      return transformToMenuItems(filteredMenuTree);
    } catch {
      return defaultMenuItems;
    }
  }, [filteredMenuTree]);

  // Calculate default open keys based on current location
  const defaultOpenKeys = useMemo(() => {
    if (filteredMenuTree.length === 0) {
      return [];
    }
    return getDefaultOpenKeys(filteredMenuTree, location.pathname);
  }, [filteredMenuTree, location.pathname]);

  const handleMenuClick: MenuProps['onClick'] = e => {
    const path = e.key;
    // Navigate to route - AppRouter will auto-update header title
    navigate(path);
  };

  // Show skeleton while permissions not yet loaded
  if (!isInitialized) {
    return (
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={toggleSidebar}
        trigger={null}
        width={250}
        collapsedWidth={80}
        className="app-sidebar"
      >
        <div className="app-sidebar__toggle" onClick={toggleSidebar}>
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </div>
        <div style={{ padding: '16px' }}>
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
        <SidebarFooter
          currentYear={currentYear}
          currentVersion={currentVersion}
        />
      </Sider>
    );
  }

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={toggleSidebar}
      trigger={null}
      width={250}
      collapsedWidth={80}
      className="app-sidebar"
    >
      {/* Collapse Toggle Button (hidden on mobile) */}
      {!isMobile && (
        <div className="app-sidebar__toggle" onClick={toggleSidebar}>
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </div>
      )}

      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        defaultOpenKeys={defaultOpenKeys}
        onClick={handleMenuClick}
        className="app-sidebar__menu"
        items={menuItems}
      />

      <SidebarFooter
        currentYear={currentYear}
        currentVersion={currentVersion}
      />
    </Sider>
  );
};

export default Sidebar;
